import { useTranslation } from 'react-i18next';
import { RhfTextField, type RhfTextFieldProps } from './shared/RhfTextField';

export type IbanFieldProps = Omit<RhfTextFieldProps, 'label'> & {
  label?: string;
};

/**
 * `IbanField` — RHF-bound IBAN input for `Bank.IBAN`.
 *
 * - **Always rendered LTR**: even in Arabic, IBANs (and most
 *   bank account identifiers) read left-to-right. Forcing
 *   direction here avoids the digits getting shuffled when the
 *   document direction is RTL.
 * - **Auto-uppercase**: SA IBANs are commonly written
 *   `SA12...`; users sometimes type `sa12...`. Normalising to
 *   uppercase keeps the stored value in canonical form.
 * - **Strip whitespace** transform — IBANs are often pasted with
 *   group spaces (`SA12 3456 7890 1234 5678 9012`). We keep them
 *   for display in the placeholder but normalise on input so
 *   server-side validation (`ibanNumberSchema`) gets the bare
 *   string it expects.
 */
export function IbanField(props: IbanFieldProps) {
  const { t } = useTranslation('m-organization');
  return (
    <RhfTextField
      {...props}
      label={props.label ?? t('form.iban_number.title')}
      placeholder={props.placeholder ?? t('form.iban_number.placeholder')}
      forceLtr
      uppercase
      transform={raw => raw.replace(/\s+/g, '')}
      maxLength={props.maxLength ?? 34}
    />
  );
}
